<template>
  <div class="user-new-agreement">
    <AppSectionHero
      :title="$t('pages.user.newAgreement.title')"
      :subtitle="$t('pages.user.newAgreement.subtitle')"
      :stats="heroStats"
      :show-refresh="false"
      @stat-click="handleHeroStatClick"
    />

    <CCard class="border-0 shadow-sm shell">
      <CCardBody class="p-4 p-lg-5">
        <div class="section-head">
          <div>
            <div class="kicker">Step 1</div>
            <h2>Agreement Setup</h2>
            <p>Start only with the agreement type. The draft method will be selected on the next screen inside the agreement editor.</p>
          </div>
        </div>

        <section ref="typeSection" class="selection-section">
          <div class="section-label">Agreement Type</div>
          <div class="option-grid">
            <button
              v-for="item in types"
              :key="item.value"
              type="button"
              class="option-card"
              :class="{ active: form.activityType === item.value }"
              @click="form.activityType = item.value"
            >
              <strong>{{ item.label }}</strong>
              <span>{{ item.description }}</span>
            </button>
          </div>
        </section>

        <div class="preview-banner">
          <div class="preview-banner__label">Selected Agreement Type</div>
          <div class="preview-banner__value">
            {{ selectedTypeLabel }}
          </div>
        </div>

        <div ref="actionRow" class="action-row">
          <CButton color="secondary" variant="outline" @click="$router.push('/dashboard/user-agreements')">Back</CButton>
          <CButton color="primary" @click="goToEditor">Continue</CButton>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import { getAgreementTypeRoute } from '@/utils/user-agreement-workflow'

export default {
  name: 'UserNewAgreement',
  components: { AppSectionHero },
  data() {
    return {
      form: {
        activityType: 'Exchange Student'
      },
      types: [
        {
          value: 'Exchange Student',
          label: 'Exchange Student',
          description: 'Student mobility, semester exchange, and short study collaboration.'
        },
        {
          value: 'Double Degree',
          label: 'Double Degree',
          description: 'Joint curriculum and coordinated dual-award academic programs.'
        },
        {
          value: 'Extension Agreement',
          label: 'Extension Agreement',
          description: 'Renew or extend an active agreement that is approaching expiry.'
        },
        {
          value: 'MOU File',
          label: 'MOU',
          description: 'General cooperation, partnership, and institutional collaboration.'
        }
      ],
    }
  },
  computed: {
    heroStats() {
      return [
        { label: 'Agreement Types', value: this.types.length, icon: 'cil-layers', iconClass: 'app-section-stat__icon--total', clickable: true },
        { label: 'Next Step', value: 'Draft Method', icon: 'cil-description', iconClass: 'app-section-stat__icon--attention', clickable: true },
        { label: 'Save Behavior', value: 'Manual Save', icon: 'cil-save', iconClass: 'app-section-stat__icon--active', clickable: true }
      ]
    },
    selectedTypeLabel() {
      const found = this.types.find(item => item.value === this.form.activityType)
      return found ? found.label : this.form.activityType
    }
  },
  methods: {
    scrollToRef(refName) {
      const target = this.$refs[refName]
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    handleHeroStatClick(stat) {
      const label = String(stat && stat.label ? stat.label : '').toLowerCase()
      if (label === 'agreement types') {
        this.scrollToRef('typeSection')
        return
      }
      if (label === 'next step') {
        this.goToEditor()
        return
      }
      if (label === 'save behavior') {
        this.scrollToRef('actionRow')
      }
    },
    goToEditor() {
      this.$router.push(`/dashboard/user-agreements/new/${getAgreementTypeRoute(this.form.activityType)}`)
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("user-new-agreement");

.shell { border-radius: 20px; }
.section-head h2 { color: #344767; margin: 6px 0; }
.section-head p { color: #718096; margin: 0; max-width: 720px; }
.kicker { color: #8c1515; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
.selection-section + .selection-section { margin-top: 24px; }
.section-label { color: #344767; font-size: 0.95rem; font-weight: 700; margin: 24px 0 12px; }
.option-grid { display: grid; gap: 14px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.option-card {
  align-items: flex-start;
  background: #fbfcfe;
  border: 1px solid #e1e8f0;
  border-radius: 18px;
  color: #344767;
  cursor: pointer;
  display: grid;
  gap: 8px;
  min-height: 112px;
  padding: 18px;
  text-align: left;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}
.option-card strong { font-size: 1rem; }
.option-card span { color: #718096; line-height: 1.45; }
.option-card.active {
  background: linear-gradient(180deg, #fff9ef 0%, #fff4df 100%);
  border-color: #d28a11;
  box-shadow: 0 16px 28px rgba(210, 138, 17, 0.14);
  transform: translateY(-1px);
}
.preview-banner {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-top: 20px;
  padding: 16px 18px;
}
.preview-banner__label { color: #718096; font-size: 0.85rem; font-weight: 700; text-transform: uppercase; }
.preview-banner__value { color: #344767; font-weight: 700; }
.action-row { display: flex; justify-content: flex-end; gap: 10px; margin-top: 24px; }
@media (max-width: 900px) {
  .option-grid { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
  .preview-banner { align-items: flex-start; flex-direction: column; }
}
</style>
